import { useCallback } from 'react'
import type { PageInput } from './types'
import { usePageFieldErrors, usePageFieldRefs } from './actions/runtime'

interface Props {
  input: PageInput
  value: string
  onChange: (next: string) => void
  layout?: 'inline' | 'stacked'
}

const inputBaseStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--pg-bg)',
  border: '1px solid var(--pg-border)',
  borderRadius: 6,
  color: 'var(--pg-text)',
  outline: 'none',
}

export default function InputRenderer({ input, value, onChange, layout = 'inline' }: Props) {
  const { errors, clearError } = usePageFieldErrors()
  const { register } = usePageFieldRefs()
  const error = errors[input.name]

  // Wrap the user-supplied onChange to auto-clear any error for this field
  // when the user starts editing it.
  const onChangeClearError = useCallback((next: string) => {
    if (error) clearError(input.name)
    onChange(next)
  }, [error, clearError, input.name, onChange])

  // Register the underlying control so the action runner's focus_input step
  // can move the cursor to it.
  const refCb = useCallback((el: HTMLElement | null) => {
    register(input.name, el)
  }, [register, input.name])

  const errorBorder: React.CSSProperties = error
    ? { border: '1px solid #ef5350', boxShadow: '0 0 0 2px rgba(239,83,80,0.12)' }
    : {}
  const styled: React.CSSProperties = { ...inputBaseStyle, ...errorBorder }

  let control: React.ReactNode

  if (input.type === 'text') {
    control = (
      <input
        ref={refCb as React.Ref<HTMLInputElement>}
        type="text"
        value={value}
        placeholder={input.placeholder}
        onChange={(e) => onChangeClearError(e.target.value)}
        style={styled}
      />
    )
  } else if (input.type === 'textarea') {
    control = (
      <textarea
        ref={refCb as React.Ref<HTMLTextAreaElement>}
        value={value}
        rows={3}
        placeholder={input.placeholder}
        onChange={(e) => onChangeClearError(e.target.value)}
        style={{ ...styled, fontFamily: 'inherit', resize: 'vertical' }}
      />
    )
  } else if (input.type === 'number' || input.type === 'spinner' || input.type === 'percentage') {
    const min = input.type === 'percentage' ? input.min ?? 0 : input.min
    const max = input.type === 'percentage' ? input.max ?? 100 : input.max
    const step = input.step ?? (input.type === 'percentage' ? 1 : undefined)
    control = (
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
        <input
          ref={refCb as React.Ref<HTMLInputElement>}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          placeholder={input.placeholder}
          onChange={(e) => onChangeClearError(e.target.value)}
          style={{ ...styled, flex: 1 }}
        />
        {(input.type === 'percentage' || input.unit) && (
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '0 10px', fontSize: '0.8625rem',
            background: 'var(--pg-surface)',
            border: '1px solid var(--pg-border)', borderRadius: 6,
            color: 'var(--pg-text-secondary)',
          }}>
            {input.type === 'percentage' ? '%' : input.unit}
          </span>
        )}
      </div>
    )
  } else if (input.type === 'slider') {
    const min = input.min ?? 0
    const max = input.max ?? 100
    const step = input.step ?? 1
    const num = value === '' || value == null ? min : Number(value)
    control = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          ref={refCb as React.Ref<HTMLInputElement>}
          type="range"
          min={min} max={max} step={step}
          value={isFinite(num) ? num : min}
          onChange={(e) => onChangeClearError(e.target.value)}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: '0.8625rem', color: 'var(--pg-text-secondary)', minWidth: 48, textAlign: 'right' }}>
          {value || min}{input.unit ? ` ${input.unit}` : ''}
        </span>
      </div>
    )
  } else if (input.type === 'range') {
    const min = input.min ?? 0
    const max = input.max ?? 100
    const step = input.step ?? 1
    const [lo, hi] = parseRange(value, String(min), String(max))
    const setRange = (next: [string, string]) => onChangeClearError(`${next[0]}|${next[1]}`)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
        <label style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)', fontWeight: 500 }}>
          {input.label || input.name}
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <input
            type="number" min={min} max={max} step={step}
            value={lo} placeholder="From"
            onChange={(e) => setRange([e.target.value, hi])}
            style={styled}
          />
          <input
            type="number" min={min} max={max} step={step}
            value={hi} placeholder="To"
            onChange={(e) => setRange([lo, e.target.value])}
            style={styled}
          />
        </div>
        {error && <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)' }}>{error}</div>}
      </div>
    )
  } else if (input.type === 'date' || input.type === 'datetime' || input.type === 'time') {
    const htmlType = input.type === 'datetime' ? 'datetime-local' : input.type
    control = (
      <input
        ref={refCb as React.Ref<HTMLInputElement>}
        type={htmlType}
        value={value}
        onChange={(e) => onChangeClearError(e.target.value)}
        style={styled}
      />
    )
  } else if (input.type === 'date_range') {
    const [start, end] = parseRange(value, '', '')
    const setRange = (next: [string, string]) => onChangeClearError(`${next[0]}|${next[1]}`)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
        <label style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)', fontWeight: 500 }}>
          {input.label || input.name}
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <input type="date" value={start} onChange={(e) => setRange([e.target.value, end])}   style={styled} />
          <input type="date" value={end}   onChange={(e) => setRange([start, e.target.value])} style={styled} />
        </div>
        {error && <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)' }}>{error}</div>}
      </div>
    )
  } else if (input.type === 'checkbox') {
    control = (
      <input
        ref={refCb as React.Ref<HTMLInputElement>}
        type="checkbox"
        checked={value === 'true'}
        onChange={(e) => onChangeClearError(e.target.checked ? 'true' : 'false')}
        style={{ width: 16, height: 16, ...(error ? { outline: '2px solid #ef5350' } : {}) }}
      />
    )
  } else if (input.type === 'select') {
    control = (
      <select
        ref={refCb as React.Ref<HTMLSelectElement>}
        value={value}
        onChange={(e) => onChangeClearError(e.target.value)}
        style={styled}
      >
        <option value="">— Select —</option>
        {(input.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    )
  } else {
    control = <span style={{ color: 'var(--pg-text-secondary)' }}>{value}</span>
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: layout === 'inline' ? 'column' : 'column',
        gap: 4,
        minWidth: 140,
      }}
    >
      <label style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)', fontWeight: 500 }}>
        {input.label || input.name}
      </label>
      {control}
      {input.helpText && !error && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--pg-text-secondary)' }}>{input.helpText}</div>
      )}
      {error && (
        <div style={{
          fontSize: '0.8125rem',
          color: 'var(--color-error, #cf222e)',
          marginTop: 2,
          lineHeight: 1.3,
        }}>{error}</div>
      )}
    </div>
  )
}

function parseRange(raw: string, fallbackLow: string, fallbackHigh: string): [string, string] {
  if (!raw) return [fallbackLow, fallbackHigh]
  const [a, b] = raw.split('|')
  return [a ?? fallbackLow, b ?? fallbackHigh]
}
